// Copyright (c) Docugami, Inc. All rights reserved.

import { Logger, LoggerCategory } from "utils/Logger";
import { TelemetryContext } from "./TelemetryContext";
import { BaseTelemetryEventSchema, TelemetryEvent, TelemetryExceptions, TelemetryPageView, WebAppUserLoggedInSchema } from "./TelemetryEvents";
import { ITelemetryProvider, TelemetryEventData, TelemetryInitializer } from "./TelemetryProvider";

export interface ITelemetry {
    register(provider: ITelemetryProvider): void;
    trackNamedEvent(name: string, data?: TelemetryEventData): void;
    trackEvent<Schema extends BaseTelemetryEventSchema<TelemetryEvent>>(schema: Schema): void;
    trackPageView(name: TelemetryPageView | string, data?: TelemetryEventData): void;
    trackException(error: Error, data?: TelemetryEventData): void;
    trackExceptionEvent(name: TelemetryExceptions, data?: TelemetryEventData): void;
    setUserContext(userId: string, accountId: string): void;
    clearUserContext(): void;
    addInitializer(initializer: TelemetryInitializer): void;
}

type EventType = 'Event' | 'PageView' | 'Exception';

export class TelemetryManager implements ITelemetry {
    private readonly _providers: ITelemetryProvider[] = [];
    private _hasUserContext: boolean = false;

    register(provider: ITelemetryProvider) {
        const existingProvider = this._providers.find((e) => {
            return e.providerId === provider.providerId;
        });

        if (!existingProvider) {
            this._providers.push(provider);
        }
    }

    // This is being deprecrated. Any new events should use trackEvent.
    /**
     * @deprecated
     */
    trackNamedEvent(name: string, data?: TelemetryEventData): void {
        Logger.LogInformation(LoggerCategory.Telemetry, this.getLogMessage('Event', name, data));
        this._providers.forEach(provider => provider.trackNamedEvent(name, data));
    }

    trackEvent<Schema extends BaseTelemetryEventSchema<TelemetryEvent>>(schema: Schema): void {
        const { eventName, ...data } = schema;
        Logger.LogInformation(LoggerCategory.Telemetry, this.getLogMessage('Event', eventName, data));
        this._providers.forEach(provider => provider.trackNamedEvent(eventName, data));
    }

    trackPageView(name: TelemetryPageView | string, data?: TelemetryEventData): void {
        Logger.LogInformation(LoggerCategory.Telemetry, this.getLogMessage('PageView', name, data));
        this._providers.forEach(provider => provider.trackPageView(name, data));
    }

    trackException(error: Error, data?: TelemetryEventData): void {
        // @ts-ignore
        if (!error.tracked) {
            // @ts-ignore
            error.tracked = true;

            Logger.LogError(LoggerCategory.Telemetry, this.getLogMessage('Exception', undefined, data), error);
            this._providers.forEach(provider => provider.trackException(error, data));
        }
    }

    trackExceptionEvent(name: TelemetryExceptions, data?: TelemetryEventData): void {
        const error = new Error(name);
        Logger.LogError(LoggerCategory.Telemetry, this.getLogMessage('Exception', undefined, data), error);
        this._providers.forEach(provider => provider.trackException(error, data));
    }

    setUserContext(userId: string, accountId: string): void {
        if (this._hasUserContext) {
            Logger.LogWarning(LoggerCategory.Telemetry, `Existing user context must be cleared before setting a new context. Id: ${userId}, AccountId: ${accountId}`);
            return;
        }

        Logger.LogInformation(LoggerCategory.Telemetry, `Setting user context. Id: ${userId}, AccountId: ${accountId}`);

        this._providers.forEach(provider => provider.setUserContext(userId, accountId));

        const appContextInitializer: TelemetryInitializer = (data) => {
            const context = TelemetryContext.context;
            for (const [key, value] of Object.entries(context)) {
                data[key] = value;
            }
        }

        this.addInitializer(appContextInitializer);

        Telemetry.trackEvent<WebAppUserLoggedInSchema>({
            eventName: TelemetryEvent.WebAppUserLoggedIn
        });

        this._hasUserContext = true;
    }

    clearUserContext(): void {
        if (this._hasUserContext) {
            Logger.LogInformation(LoggerCategory.Telemetry, `Clearing user context.`);
            this._providers.forEach(provider => provider.clearUserContext());
            this._hasUserContext = false;
        }
    }

    addInitializer(initializer: TelemetryInitializer): void {
        this._providers.forEach(provider => provider.addInitializer(initializer));
    }

    private getLogMessage(eventType: EventType, name?: string, data?: TelemetryEventData) {
        let log = `Track ${eventType}`;
        if (!!name) {
            log += ` | Name: ${name}`;
        }

        if (!!data) {
            log += ` | Data: ${JSON.stringify(data)}`
        }

        return log;
    }
}

const Telemetry: ITelemetry = new TelemetryManager();

export { Telemetry }
